"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RESOURCE_LOADS = exports.resourceViewSchema = exports.resourceLineItemSchema = exports.resourceRowSchema = exports.resourceItemSchema = exports.resourceColumnSchema = exports.RESOURCE_MODES = void 0;
exports.loadOf = loadOf;
const zod_1 = require("zod");
const allocation_1 = require("../delivery/allocation");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The Erőforrás dashboard — C53, Phase 4. People × time, planned.           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THREE LINES PER PERSON, AND THE MIDDLE ONE IS THE ONLY ONE ANYBODY EDITS.** *Munkaidő* is
 * what the schedule and the calendar leave them, *Kiosztott* is what has been planned onto them,
 * *Szabadság* is what they are away for. Collapsing the first and the third into one *available*
 * figure was the obvious simplification and is wrong: a week with no capacity because somebody is
 * on holiday and a week with no capacity because nobody gave them a schedule are different
 * problems with different answers, and the grid has to say which.
 *
 * ⚠️ **Every figure is a STRING**, like every other `numeric` on the wire.
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
exports.RESOURCE_MODES = ['week', 'day'];
/**
 * ⚠️ **The heading's TEXT is not here.** `top` and `sub` come back as data the screen formats —
 * a date range and an ISO week number — because a weekday's name and a month's abbreviation are
 * language, and rule 7 puts those in the catalogue rather than in a response body.
 */
exports.resourceColumnSchema = zod_1.z.object({
    key: zod_1.z.string(),
    from: wireDateSchema,
    to: wireDateSchema,
    /** The ISO week number, in week mode. Null in day mode, where the heading is the date itself. */
    week: zod_1.z.number().int().nullable(),
    current: zod_1.z.boolean(),
});
/**
 * One line item a person is planned onto, with what each column holds.
 *
 * ⚠️ **`cells` is per COLUMN and in the same order** — the grid is a table and a map keyed by date
 * would make every cell a lookup that can silently miss.
 */
exports.resourceItemSchema = zod_1.z.object({
    allocationId: zod_1.z.string(),
    lineItemId: zod_1.z.string(),
    /**
     * ⚠️⚠️ **ITS NUMBER, WHICH IS HALF OF ITS ADDRESS.** A line item lives at
     * `/contracts/:code/:number`, so the row's summary pop-over could not offer *Sortétel
     * megnyitása* — it held the id and the contract code and no way to build the link. Reported
     * as the missing half of item 28.
     *
     * ⚠️ **Nullable beside `contractCode`**, and for the same reason: a booking may hang off a
     * deliverable whose contract the reader cannot see, and half an address is no address.
     */
    lineItemNumber: zod_1.z.number().int().nullable(),
    lineItemName: zod_1.z.string(),
    projectId: zod_1.z.string().nullable(),
    projectName: zod_1.z.string().nullable(),
    projectColor: zod_1.z.string().nullable(),
    contractCode: zod_1.z.string().nullable(),
    /** R-57 · the line item's and contract's public identifiers — the address the screens link to. */
    lineItemIdentifier: zod_1.z.string().nullable(),
    contractIdentifier: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **WHO THE CONTRACT ANSWERS TO — round 5 item 5.** The resource row read
     * *Riport modul / Haystack · HSO-C01*; a contract code is a machine identifier, and the thing
     * somebody scanning a person's week actually wants beside the project is the name of whoever
     * owns the work. `contractCode` stays on the wire because other readers use it.
     */
    contractOwnerName: zod_1.z.string().nullable(),
    /** The overtime type planned on this allocation, and its rate. Null on an ordinary one. */
    overtimeTypeName: zod_1.z.string().nullable(),
    overtimeTypeId: zod_1.z.string().nullable(),
    multiplier: zod_1.z.string(),
    /**
     * ⚠️ **The two the cell pop-over sets, which the grid could not show until it had one.** They
     * are properties of the ALLOCATION rather than of the column somebody clicked — one release
     * week's rate for the whole booking — and they travel so the pop-over can open without a second
     * read per cell.
     */
    distribution: zod_1.z.enum(allocation_1.ALLOCATION_DISTRIBUTIONS),
    booking: zod_1.z.enum(allocation_1.ALLOCATION_BOOKINGS),
    /** The line item's own estimate, so a row can say what share of it the plan has taken. */
    estimateHours: zod_1.z.string(),
    /** Everything planned against this line item by this person, across the WHOLE allocation. */
    plannedHours: zod_1.z.string(),
    /**
     * ┌────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ THE ROW'S OWN SUMMARY — round 5 items 28 and 29.                      │
     * └────────────────────────────────────────────────────────────────────┘
     *
     * The mockup draws an info pop-over on every line-item row: the dates the booking runs between,
     * what was estimated, what is planned, what has been logged, what is left, and whether any of it
     * is at an overtime rate. The screenshot that came with round 5 is the specification.
     *
     * ⚠️⚠️ **THEY RIDE WITH THE ROW RATHER THAN BEING FETCHED ON HOVER.** A read per pop-over
     * would be a request every time somebody's mouse crossed a row, on a grid built to be scanned —
     * and item 29 asks for the same eight figures for EVERY line item at once, which a per-row
     * endpoint could not serve without N calls.
     *
     * ⚠️ **`fromDate` / `toDate` are the ALLOCATION's window**, not the line item's: the question
     * the pop-over answers is *when is this person on this work*.
     */
    fromDate: zod_1.z.string().nullable(),
    toDate: zod_1.z.string().nullable(),
    /** What this person has actually logged against this line item's tasks. */
    trackedHours: zod_1.z.string(),
    /** Of the planned hours, how many sit on an allocation carrying an overtime type. */
    overtimeHours: zod_1.z.string(),
    /**
     * ⚠️ **Estimate minus logged, floored at zero.** Not estimate minus PLANNED: the mockup's
     * *Maradék* sits under *Rögzített* and answers *how much of the estimate is still unspent*.
     * Negative would mean the estimate was passed, which the figure above it already says.
     */
    remainingHours: zod_1.z.string(),
    /** *Fix díjas*, *Ismétlődő*, … — the badge in the pop-over's header. */
    contractType: zod_1.z.string().nullable(),
    cells: zod_1.z.array(zod_1.z.string()),
});
exports.resourceRowSchema = zod_1.z.object({
    memberId: zod_1.z.string(),
    memberName: zod_1.z.string(),
    memberAvatarUrl: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from**, when there is no picture
     * above. The normalised email, produced by `wireAvatarSeed` — nullable exactly like the URL
     * beside it, because a row with nobody in it has no face either.
     */
    memberAvatarSeed: zod_1.z.string().nullable(),
    /** The default project role — *Tech Lead*, *QA*. The mockup's `drole`, not a permission. */
    roleName: zod_1.z.string().nullable(),
    /** Per column, in order. */
    capacity: zod_1.z.array(zod_1.z.string()),
    allocated: zod_1.z.array(zod_1.z.string()),
    leave: zod_1.z.array(zod_1.z.string()),
    /**
     * ⚠️ **Billed overtime inside each column**, which is what puts the dot on a cell. Separate from
     * `allocated` because the two answer different questions: *how much of their time is spoken for*
     * and *how much of it is being planned at a premium*.
     */
    overtime: zod_1.z.array(zod_1.z.string()),
    /** ⚠️ Whether ANY column is over capacity — the warning triangle beside the name. */
    overPlanned: zod_1.z.boolean(),
    underPlanned: zod_1.z.boolean(),
    items: zod_1.z.array(exports.resourceItemSchema),
    /**
     * Phase 6 · DM-5 · the person's TENTATIVE bookings — open deals' planned hours on a placeholder
     * that names them. ⚠️ Absent unless asked for (`?tentative=1`), and NEVER part of `allocated`,
     * the load verdicts or the stats: a deal that has not been won books nobody.
     */
    tentative: zod_1.z
        .array(zod_1.z.object({
        dealId: zod_1.z.string(),
        dealIdentifier: zod_1.z.string(),
        dealName: zod_1.z.string(),
        cells: zod_1.z.array(zod_1.z.string()),
    }))
        .optional(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ONE OF A PROJECT'S LINE ITEMS, WHETHER OR NOT ANYBODY IS ON IT.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"The hint button … doesn't show all line items, only the ones relevant by the time period."*
 * The overview used to fold the grid's own items, which are bounded twice over — by the window and
 * by who has a booking — so a deliverable nobody was planned onto yet, or one planned for next
 * quarter, never appeared in a table whose title says *every line item*. These are read from the
 * project's contracts instead, and every figure is the WHOLE line item, like `projectTotals`.
 *
 * ⚠️ **A licence is not here** — it takes no work (U-7, U-19), so it has nothing to plan or log.
 * ⚠️ **A closed one is, marked `closed`** — closing blocks nothing (U-17) and its hours still
 * count toward every total above it.
 */
exports.resourceLineItemSchema = zod_1.z.object({
    lineItemId: zod_1.z.string(),
    lineItemName: zod_1.z.string(),
    lineItemNumber: zod_1.z.number().int().nullable(),
    lineItemIdentifier: zod_1.z.string().nullable(),
    contractIdentifier: zod_1.z.string().nullable(),
    contractCode: zod_1.z.string().nullable(),
    contractOwnerName: zod_1.z.string().nullable(),
    contractType: zod_1.z.string().nullable(),
    projectId: zod_1.z.string().nullable(),
    projectName: zod_1.z.string().nullable(),
    projectColor: zod_1.z.string().nullable(),
    closed: zod_1.z.boolean(),
    /** The widest window any booking on it covers — null while nobody is planned onto it. */
    fromDate: zod_1.z.string().nullable(),
    toDate: zod_1.z.string().nullable(),
    /**
     * 17 Oct 2026 · the line item's OWN dates — *Kezdés* and *Vég* on the contract page. The overview
     * lists by them, always in date order (the product owner).
     */
    startsOn: zod_1.z.string().nullable(),
    endsOn: zod_1.z.string().nullable(),
    estimateHours: zod_1.z.string(),
    plannedHours: zod_1.z.string(),
    /** Of the planned hours, how many sit on a booking carrying an overtime type. */
    overtimeHours: zod_1.z.string(),
    /** Everything logged against its tasks, by anybody. */
    trackedHours: zod_1.z.string(),
    /** Estimate minus logged, floored at zero — `resourceItemSchema.remainingHours`'s rule. */
    remainingHours: zod_1.z.string(),
    /**
     * ⚠️ **Everybody with a booking on it, at any time** — planned or not, member or not. Its length
     * is the overview's *Emberek*; the line-item grid draws a sub-row for each who has a row.
     */
    memberIds: zod_1.z.array(zod_1.z.string()),
    /**
     * ⚠️ 3 Nov 2026 · F4.4 · **every booking on it, by id** — whoever holds it and where its span
     * ends. A person whose only booking lies outside the window is drawn as a placeholder line, and
     * without the id that line could offer *delete the whole allocation* and do nothing. Optional so
     * a reader of an older answer keeps working; the server always sends it.
     */
    bookings: zod_1.z
        .array(zod_1.z.object({
        allocationId: zod_1.z.string(),
        memberId: zod_1.z.string(),
        fromDate: zod_1.z.string().nullable(),
        toDate: zod_1.z.string().nullable(),
    }))
        .optional(),
});
exports.resourceViewSchema = zod_1.z.object({
    from: wireDateSchema,
    to: wireDateSchema,
    mode: zod_1.z.enum(exports.RESOURCE_MODES),
    columns: zod_1.z.array(exports.resourceColumnSchema),
    rows: zod_1.z.array(exports.resourceRowSchema),
    /**
     * ⚠️⚠️ **THE PROJECT TAB'S FOUR FIGURES, AND NULL EVERYWHERE ELSE.** They are sums over a
     * PROJECT'S line items — its estimate, what has been planned against it and what has been logged
     * — so they have no meaning on the workspace grid, where the rows come from every project at
     * once. `null` says *this view is not about one project* rather than making the tab check a
     * filter it did not set.
     *
     * ⚠️ **These are the WHOLE project, not the window.** A remaining-plan figure bounded by the
     * six weeks on screen would fall every time somebody scrolled forward.
     */
    projectTotals: zod_1.z
        .object({
        estimateHours: zod_1.z.string(),
        plannedHours: zod_1.z.string(),
        trackedHours: zod_1.z.string(),
        remainingHours: zod_1.z.string(),
    })
        .nullable(),
    /**
     * ⚠️ **The project's line items, every one — and null off a project's tab**, for the reason
     * `projectTotals` gives. Not bounded by the window, the search or the load filters.
     */
    projectLineItems: zod_1.z.array(exports.resourceLineItemSchema).nullable(),
    /**
     * ⚠️⚠️ **WHO IS ON THE PROJECT TODAY — P-1, and null off a project's tab.** The rows are the team
     * OR anybody already planned onto it (see `resource.service.ts`), so a row is not proof of
     * membership; *Személy hozzáadása* offers only these, because the server refuses the rest.
     */
    projectMemberIds: zod_1.z.array(zod_1.z.string()).nullable(),
    /**
     * The six figures across the top. ⚠️ **Computed over the rows the FILTERS left**, not over the
     * workspace: the strip is a summary of what is on the screen, and a total that ignored the
     * filters would contradict the grid under it.
     */
    stats: zod_1.z.object({
        people: zod_1.z.number().int(),
        under: zod_1.z.number().int(),
        over: zod_1.z.number().int(),
        /** Allocated over capacity, as a whole percentage. Null when nobody has any capacity at all. */
        averageLoad: zod_1.z.number().int().nullable(),
        plannedHours: zod_1.z.string(),
        overtimeHours: zod_1.z.string(),
    }),
});
/**
 * ⚠️ **The verdict a cell is painted by.** `none` is *this person cannot work at all in this
 * column* — a holiday week, or somebody with no schedule — which is not the same as being
 * under-planned, and painting it blue would tell a planner to give them work they cannot take.
 */
exports.RESOURCE_LOADS = ['none', 'under', 'ok', 'over'];
/**
 * ⚠️⚠️ **HALF AN HOUR OF SLACK, AND THE MOCKUP'S OWN FIGURE.** Without it a 39:45 week against a
 * 40:00 capacity reads as under-planned and the whole grid lights up blue over rounding — the
 * quarter-hour distribution rule guarantees remainders that small on almost every row.
 */
function loadOf(allocated, capacity) {
    if (capacity <= 0)
        return allocated > 0 ? 'over' : 'none';
    if (allocated > capacity + 0.5)
        return 'over';
    if (allocated < capacity - 0.5)
        return 'under';
    return 'ok';
}
//# sourceMappingURL=resource.js.map